import React, { useEffect, useState } from 'react';
import { Container, Nav, Stack, Form, Tab, Tabs } from 'react-bootstrap';
import ConnectionRequestBox from 'components/ConnectionRequestBox';
import QuickLinksBox from 'components/QuickLinksBox';
import Profile from 'components/UserBox/Profile';
import LeftMenus from 'components/UserBox/LeftMenus';
import { useGetBlockListQuery, useGetMyConnectionListQuery } from 'services';
import { useQueryClient } from '@tanstack/react-query';
import MyConnectionComponent from 'components/Connections/MyConnections/MyConnections';
import PaginationBox from 'components/PaginationBox';
import MyRequestedUsers from 'components/Connections/RequestedUsers/MyRequestedUsers';
import BlockedUsers from 'components/Connections/BlockedUsers/BlockedUser';
import { useSearchParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { dataLimits } from 'constants';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const MyConnections: React.FC = () => {
	const { translate } = useTranslate();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTabKey = searchParams.get('activeTab') ?? 'connected';
	const userId = searchParams.get('userId') ?? '';
	const [startSearch, setStartSearch] = useState<boolean>(false);

	const [userData, setUserData] = useState({
		currentPage: 1,
		limit: dataLimits.limits,
		order: 'DESC',
		connectionType: activeTabKey,
		keyword: searchParams.get('value') ?? '',
		userId: userId,
	});

	const { data = [] }: any = useGetMyConnectionListQuery(userData);
	const { data: blockUserData = [] }: any = useGetBlockListQuery(userData);

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}, [activeTabKey]);

	useEffect(() => {
		if (userData?.connectionType === 'blockedusers') {
			queryClient.invalidateQueries({ queryKey: [`get-block-users`] });
		} else {
			queryClient.invalidateQueries({
				queryKey: [`get-my-connection-${userData?.connectionType}`],
			});
		}
		window.scrollTo({
			top: 0,
			behavior: 'instant',
		});
	}, [userData.currentPage, startSearch, userId]);

	const handleSearch = (e: any) => {
		if (e.keyCode === 13 && e.target.value) {
			searchParams.set('value', e.target.value.trim());
			setSearchParams(searchParams);
			setStartSearch((prev) => !prev);
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value.trim(),
			}));
		}
	};

	const handleOnChange = (e: any) => {
		setSearchParams({ activeTab: userData?.connectionType, value: e.target.value });
		if (e?.target?.value.length === 0) {
			setStartSearch((prev) => !prev);
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		} else {
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		}
	};

	const handleTabChange = (e: any) => {
		setUserData((prev) => ({
			...prev,
			connectionType: e,
			currentPage: 1,
		}));
		setSearchParams({ activeTab: e, value: userData?.keyword ?? '', userId: userId || '' });
	};

	const handleCurrentPage = (e: any) => {
		setUserData((prev) => ({
			...prev,
			currentPage: e,
		}));
	};

	const refetchListData = (type: string) => {
		setUserData((prev) => ({
			...prev,
			currentPage: 1,
			connectionType: type,
		}));

		if (userData?.currentPage === 1) {
			queryClient.invalidateQueries({
				queryKey: [`get-my-connection-${userData?.connectionType}`],
			});
		}
	};

	const refetchBlockedListData = (type: string) => {
		setUserData((prev) => ({
			...prev,
			currentPage: 1,
			connectionType: type,
		}));

		if (userData?.currentPage === 1) {
			queryClient.invalidateQueries({ queryKey: [`get-block-users`] });
		}
	};

	return (
		<div className="white-box bg-white">
			<Stack className="gap-3 gap-md-4">
				<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
					<div className="list-heading">
						<h4 className="fw-bold">{translate('Common.myConnections')}</h4>
					</div>
					<Form.Group className="user-search position-relative w-100">
						<i className="ri-search-line position-absolute"></i>
						<Form.Control
							className="rounded-pill"
							type="search"
							placeholder={translate('PlaceHolder.searchByUserName')}
							onKeyDown={handleSearch}
							onChange={handleOnChange}
							value={userData.keyword ?? ''}
						/>
					</Form.Group>
				</div>
				<div className="connections-tab">
					<Tabs activeKey={activeTabKey} id="controlled-tab-example" onSelect={handleTabChange}>
						<Tab eventKey="connected" title={translate('Social.connections')}>
							<MyConnectionComponent
								connectionData={data}
								refetchListData={refetchListData}
								otherData={userData}
								handleCurrentPage={handleCurrentPage}
							/>
						</Tab>
						<Tab eventKey="request" title={translate('Social.request')}>
							<MyRequestedUsers
								connectionData={data}
								refetchListData={refetchListData}
								otherData={userData}
								handleCurrentPage={handleCurrentPage}
							/>
						</Tab>
						<Tab eventKey="blockedusers" title={translate('Social.blockedUsers')}>
							<BlockedUsers
								connectionData={blockUserData}
								refetchBlockedListData={refetchBlockedListData}
								otherData={userData}
								handleCurrentPage={handleCurrentPage}
							/>
						</Tab>
					</Tabs>
				</div>
			</Stack>
			{!['blockedusers'].includes(userData.connectionType) ? (
				<PaginationBox
					totalData={data?.meta?.totalDocs}
					limit={data?.meta?.limit}
					page={userData.currentPage}
					setCurrentPage={handleCurrentPage}
				/>
			) : (
				<PaginationBox
					totalData={blockUserData?.meta?.totalDocs}
					limit={blockUserData?.meta?.limit}
					page={userData.currentPage}
					setCurrentPage={handleCurrentPage}
				/>
			)}
		</div>
	);
};

export default MyConnections;
